import { ActivityType } from '../service/ActivityFeedStorageItem';
import {
    ActivityFeedItem,
    LikeActivityFeedItem,
    MentionActivityFeedItem,
    UserAtMentionedActivityFeedItem,
    FlightReservationActivityFeedItem,
} from '../service/ActivityFeedItem';
import {
    activityFeedV2LikeCardBody,
    activityFeedV2TwoOrMoreLikesCardBody,
    activityFeedV2MentionCardBody,
    activityFeedV2UserAtMentionedCardBody,
    activityFeedV2FlightReservationCardBody,
    activityFeedV2FlightHeroActionText,
} from './activityFeedCard.locstring.json';
import { ActivityFeedActionType } from '../components/ActivityFeedActions';
import { ControlIcons } from 'owa-control-icons';
import loc, { format } from 'owa-localize';
import { ActivityFeedPersonaControlProp } from '../components/ActivityFeedPersonaControl';
import encode from 'owa-encoding-utils/lib/utils/html/encode';
import { formatMonthDayShortTime, asOwaDateInUserTz } from 'owa-datetime';
export interface ActivityFeedCardDetailsProps {
    item: ActivityFeedCardDetails;
    onAction: (itemId: string, action: ActivityFeedActionType) => void;
    itemId: string;
}

export interface ActivityFeedCardDetails {
    senderInfo: ActivityFeedPersonaControlProp[];
    messageId: string;
    cardTitle: string;
    cardIcon: string;
    isSeen: boolean;
    timeStamp: Date;
    cardType: ActivityCardLayoutType;
    activityType: ActivityType;
    heroButtonProperties?: HeroActionProperties;
}

export interface HeroActionProperties {
    buttonTitle: string;
    buttonActionUrl: string;
}

export enum ActivityCardLayoutType {
    SingleTitleActivityCard = 0,
    MultiTitleActivityCard = 1,
    HeroActionCard = 2,
}

export function getActivityCardPresentationDetails(item: ActivityFeedItem) {
    switch (item.type) {
        case ActivityType.Like:
            return getLikeFeedCard(item);
        case ActivityType.AtMention:
            return getAtMentionFeedCard(item);
        case ActivityType.UserAtMentioned:
            return getUserAtMentionFeedCard(item);
        case ActivityType.TxpFlightReservation:
            return getFlightReservationFeedCard(item);
    }
    return {} as ActivityFeedCardDetails;
}

function getFlightReservationFeedCard(
    item: FlightReservationActivityFeedItem
): ActivityFeedCardDetails {
    const departureTimestamp = formatMonthDayShortTime(
        asOwaDateInUserTz(new Date(item.payload.reservationFor.departureTime))
    );
    const titleText = format(
        loc(activityFeedV2FlightReservationCardBody),
        formatBoldMarkdownString(departureTimestamp)
    );
    return {
        senderInfo: [{ name: item.payload.senderName, emailAddress: item.payload.senderEmail }],
        messageId: item.payload['originEmails/Txp'][0].internetMessageId,
        cardTitle: titleText,
        timeStamp: item.timestamp,
        isSeen: item.isSeen,
        cardType: ActivityCardLayoutType.SingleTitleActivityCard,
        activityType: ActivityType.TxpFlightReservation,
        cardIcon: ControlIcons.AirplaneSolid,
        heroButtonProperties: {
            buttonTitle: loc(activityFeedV2FlightHeroActionText),
            buttonActionUrl: item.payload.checkinUrl,
        } as HeroActionProperties,
    } as ActivityFeedCardDetails;
}

function getLikeFeedCard(item: LikeActivityFeedItem): ActivityFeedCardDetails {
    let titleText = '';
    const encodedSubject = encode(item.payload.TargetSubject);
    if (item.payload.Actors.length == 1) {
        titleText = format(
            loc(activityFeedV2LikeCardBody),
            item.payload.Actors[0].EmailAddress.Name,
            encodedSubject
        );
    } else {
        titleText = format(
            loc(activityFeedV2TwoOrMoreLikesCardBody),
            item.payload.Actors.length.toString(),
            encodedSubject
        );
    }
    var senderInfo = item.payload.Actors.map(function(actor) {
        return { name: actor.EmailAddress.Name, emailAddress: actor.EmailAddress.EmailAddress };
    });
    return {
        senderInfo: senderInfo,
        messageId: item.payload.TargetLogicalId,
        cardTitle: titleText,
        timeStamp: item.timestamp,
        isSeen: item.isSeen,
        cardType: ActivityCardLayoutType.SingleTitleActivityCard,
        activityType: ActivityType.Like,
        cardIcon: ControlIcons.Like,
    } as ActivityFeedCardDetails;
}

function getAtMentionFeedCard(item: MentionActivityFeedItem): ActivityFeedCardDetails {
    const encodedSubject = encode(item.payload.Subject);
    const cardTitle = format(
        loc(activityFeedV2MentionCardBody),
        formatBoldMarkdownString(item.payload.Actor.EmailAddress.Name),
        encodedSubject
    );
    return {
        senderInfo: [
            {
                name: item.payload.Actor.EmailAddress.Name,
                emailAddress: item.payload.Actor.EmailAddress.EmailAddress,
            },
        ],
        messageId: item.payload.InternetMessageId,
        cardTitle: cardTitle,
        timeStamp: item.timestamp,
        isSeen: item.isSeen,
        cardType: ActivityCardLayoutType.MultiTitleActivityCard,
        activityType: ActivityType.AtMention,
        cardIcon: ControlIcons.Accounts,
    } as ActivityFeedCardDetails;
}

function formatBoldMarkdownString(text: string): any {
    let trimmedText = text.trim();
    if (trimmedText == '') {
        return trimmedText;
    } else {
        return `**${trimmedText}**`;
    }
}

function getUserAtMentionFeedCard(item: UserAtMentionedActivityFeedItem): ActivityFeedCardDetails {
    const encodedFilename = encode(item.payload.SourceFileName);
    var cardTitle = format(
        loc(activityFeedV2UserAtMentionedCardBody),
        item.payload.Mentioner.DisplayName,
        encodedFilename
    );
    return {
        senderInfo: [
            {
                name: item.payload.Mentioner.DisplayName,
                emailAddress: item.payload.Mentioner.Email,
            },
        ],
        messageId: item.payload.ObjectId,
        cardTitle: cardTitle,
        timeStamp: item.timestamp,
        isSeen: item.isSeen,
        cardType: ActivityCardLayoutType.SingleTitleActivityCard,
        activityType: ActivityType.UserAtMentioned,
        cardIcon: ControlIcons.Accounts,
    } as ActivityFeedCardDetails;
}
